import '../../styles/restScreens.css';
import { useCallback, useEffect, useState } from 'react';
import { supabaseClient } from '../../config/supabase';

const ACTION_LABELS = {
    grant_created: 'Grant created',
    code_redeemed: 'Code redeemed',
    session_entered: 'Session entered',
    session_exited: 'Session exited',
    grant_revoked: 'Grant revoked',
    escalated: 'Escalated'
};

const ACTION_STYLES = {
    grant_created: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    code_redeemed: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
    session_entered: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
    session_exited: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
    grant_revoked: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
    escalated: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300'
};

function formatDate(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString();
}

function shortId(value) {
    return value ? `${value.slice(0, 8)}…` : '—';
}

function DetailSummary({ detail }) {
    if (!detail || Object.keys(detail).length === 0) return <span className="text-gray-400">—</span>;
    if (detail.issue_url) {
        return (
            <a
                href={detail.issue_url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-blue-600 dark:text-blue-300 hover:underline"
            >
                Issue created
            </a>
        );
    }
    return (
        <span className="font-mono text-xs break-all text-gray-500 dark:text-gray-400">
            {JSON.stringify(detail)}
        </span>
    );
}

/**
 * SupportAuditLogView — shared read surface for the support-access audit log.
 *
 * Props:
 *   orgId   — when provided, restricts to that org's rows (tenant view).
 *             Pass null / undefined for platform-staff view (all orgs).
 *   grantId — optional filter to a single grant.
 *   dark    — dark-mode flag.
 */
function SupportAuditLogView({ orgId, grantId, dark = false }) {
    const textClass = dark ? 'text-gray-100' : 'text-gray-900';
    const mutedClass = dark ? 'text-gray-400' : 'text-gray-600';
    const cardClass = 'bg-[var(--surface-raised)] border-[var(--surface-border)]';

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterGrantId, setFilterGrantId] = useState(grantId || '');

    const loadRows = useCallback(async () => {
        setLoading(true);
        setError(null);

        let query = supabaseClient
            .from('support_access_audit_log')
            .select('id, grant_id, org_id, actor_user_id, action, detail, created_at')
            .order('created_at', { ascending: false })
            .limit(200);

        if (orgId) {
            query = query.eq('org_id', orgId);
        }
        if (filterGrantId.trim()) {
            query = query.eq('grant_id', filterGrantId.trim());
        }

        const { data, error: queryError } = await query;

        if (queryError) {
            setError(queryError.message);
            setRows([]);
        } else {
            setRows(data || []);
        }
        setLoading(false);
    }, [orgId, filterGrantId]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    return (
        <div data-m6-layout="support" data-m6-component="SupportAuditLogView" className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
                <label className={`flex items-center gap-2 text-sm ${textClass}`}>
                    <span className="font-medium">Filter by grant ID</span>
                    <input
                        type="text"
                        value={filterGrantId}
                        onChange={(e) => setFilterGrantId(e.target.value)}
                        placeholder="Grant UUID (optional)"
                        className={`rounded-lg border px-3 py-1.5 text-sm font-mono w-72 ${
                            dark
                                ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
                                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                        }`}
                    />
                </label>
                <button
                    type="button"
                    onClick={loadRows}
                    className="rounded-lg px-3 py-1.5 text-sm font-medium bg-orange-600 hover:bg-orange-700 text-white"
                >
                    Refresh
                </button>
            </div>

            {error && (
                <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/40 px-4 py-3 text-sm text-red-800 dark:text-red-300">
                    {error}
                </div>
            )}

            <div className={`rounded-xl border overflow-hidden ${cardClass}`}>
                {loading ? (
                    <div className={`p-8 text-sm ${mutedClass}`}>Loading audit log…</div>
                ) : rows.length === 0 ? (
                    <div className={`p-8 text-sm ${mutedClass}`}>No audit log entries found.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className={dark ? 'bg-gray-700/50' : 'bg-gray-50'}>
                                <tr>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Action</th>
                                    {!orgId && (
                                        <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Org</th>
                                    )}
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Grant</th>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Actor</th>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Timestamp</th>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Detail</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${dark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                                {rows.map((row) => (
                                    <tr key={row.id} className={dark ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50'}>
                                        <td className="px-5 py-3">
                                            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${ACTION_STYLES[row.action] || ''}`}>
                                                {ACTION_LABELS[row.action] || row.action}
                                            </span>
                                        </td>
                                        {!orgId && (
                                            <td className={`px-5 py-3 font-mono text-xs ${mutedClass}`}>{shortId(row.org_id)}</td>
                                        )}
                                        <td className={`px-5 py-3 font-mono text-xs ${mutedClass}`}>{shortId(row.grant_id)}</td>
                                        <td className={`px-5 py-3 font-mono text-xs ${mutedClass}`}>{shortId(row.actor_user_id)}</td>
                                        <td className={`px-5 py-3 text-xs ${mutedClass}`}>{formatDate(row.created_at)}</td>
                                        <td className="px-5 py-3 text-xs max-w-xs">
                                            <DetailSummary detail={row.detail} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default SupportAuditLogView;
